import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RecordContext } from "../../components/record/Record";
import { InstrumentDimension } from "./psyEvaluations.types";
import { api } from "../../api/config";
import { useRelatedRecords } from "../../api/useRelatedRecords";
import { useStore } from "../../store/store";

export const useInstrumentResponseCreateForm = () => {
	const recordContext = useContext(RecordContext);

	const entityForm = recordContext?.entityForm;
	const navigate = useNavigate();
	const { notifications } = useStore();
	const queryClient = useQueryClient();

	const watchedStatus = entityForm?.watch("status");
	const selectedInstrument = entityForm?.watch("instrument_id");
	const instrumentId = selectedInstrument?.id;

	const { data: dimensionsData, status: dimensionsStatus } = useRelatedRecords(
		"instrument_dimensions",
		"id,name,description",
		"instrument",
		instrumentId,
		undefined,
		{ extraOptions: { sort: "sort" } },
	);

	const dimensions = (dimensionsData?.items ?? []) as Array<InstrumentDimension>;

	useEffect(() => {
		if (!watchedStatus || !entityForm) return;
		const currentTimestamp = new Date().toISOString();

		entityForm.setValue("completed_at", watchedStatus === "completed" ? currentTimestamp : null, { shouldDirty: true });
		entityForm.setValue("sent_at", watchedStatus === "sent" ? currentTimestamp : null, { shouldDirty: true });
	}, [watchedStatus, entityForm]);

	const onSubmit = async (formData: any) => {
		const rawScores = formData.scores || {};

		if (!formData.treatment && !formData.lead) {
			notifications.setNotification({
				isOpen: true,
				message: "Selecione um tratamento ou um lead.",
				severity: "error",
			});
			return;
		}

		if (instrumentId && dimensionsStatus !== "success") {
			notifications.setNotification({
				isOpen: true,
				message: "Aguarde o carregamento das dimensões do instrumento.",
				severity: "error",
			});
			return;
		}

		const payload = {
			instrument_id: formData.instrument_id?.id ?? null,
			treatment: formData.treatment?.id ?? null,
			lead: formData.lead?.id ?? null,
			status: formData.status ?? null,
			completed_at: formData.completed_at ?? null,
			responses_scores: dimensions.map((dimension) => {
				const rawScore = rawScores[dimension.id];
				return {
					instrument_dimension: dimension.id,
					raw_score: rawScore === "" || rawScore == null ? null : Number(rawScore),
				};
			}),
		};

		try {
			const { data } = await api.post("/instruments-forms/crm-responses/scored", payload);

			queryClient.invalidateQueries({
				predicate: (query: any) => query.queryKey[0]?.startsWith?.("instruments_responses"),
			});
			notifications.setNotification({
				isOpen: true,
				message: "Avaliação criada com sucesso.",
				severity: "success",
			});
			navigate(`/instruments_responses/${data.response_id}/detail`);
		} catch (error: any) {
			notifications.setNotification({
				isOpen: true,
				message: error?.response?.data?.errors?.[0]?.message || error?.response?.data?.errorMessage || "Erro ao criar avaliação.",
				severity: "error",
			});
		}
	};

	return {
		entityForm,
		formatedFields: recordContext?.formatedFields ?? [],
		dimensions,
		dimensionsStatus,
		instrumentId,
		onSubmit,
	};
};
